import {
    React,
    CustomDataGrid, CustomButton, CustomTextField,
    useState, useApi, useEffect,
    Box, Dialog, CloseIcon, DialogContent, DialogTitle, Divider,
    Dayjs, dayjs,
    Grid2, GridColDef,
    IconButton, EditCalendarIcon,
    Typography, Toaster,
    validationMessages, ForecastDisplayType,
} from '../../../utils/globalImports';

import {
    ProjectBaselineResponse,
    BaselineCommentType,
    BaselineCommentTypeLabels
} from "../../../interfaces/baselineInterface";

import { FeeStageComment } from '../../../interfaces/forecast.interface';
import { ApiResponse } from '../../../services/apiService';

const typeColors: Record<number, string> = {
    [BaselineCommentType.PM]: "#A3C63D",           // green
    [BaselineCommentType.CommercialLead]: "#3D9BC6", // blue
    [BaselineCommentType.SMT]: "#E5A53F",          // amber
};

interface ModalProps {
    open: boolean;
    onClose: (commentAdded: boolean) => void;
    feestage: FeeStageComment;
    monthYear?: string;
    forecastType: number;
    isBaselineComment?: boolean;
    baselineCommentType?: number;
}

interface Note {
    commentID?: number;
    feeStageId?: number;
    monthYear?: string;
    commentText: string;
    createdByName?: string;
    createdDate?: Dayjs;
    updatedByName?: string;
    updatedDate?: Dayjs;
    isBaselineComment?: boolean;
    baselineCommentType?: number;
}

const ForecastComment: React.FC<ModalProps> = ({ open, onClose, feestage, monthYear, forecastType, isBaselineComment, baselineCommentType }) => {

    const [formData, setFormData] = useState<Note>({ commentText: '' });
    const [errors, setErrors] = useState({ description: '' });
    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const [dataList, setDataList] = useState<any[]>([]);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const { execute, loading } = useApi();

    const userId = Number(localStorage.getItem("userId"));
    // Delay function
    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        if (feestage.hasComments)
            fetchComments();
    }, [])

    // Fetch Notes/Comments
    const fetchComments = async () => {
        const url =
            forecastType === ForecastDisplayType.OutStanding
                ? `/Project/GetOutstanding-comments?feeStageId=${feestage.id}&displayType=${forecastType}`
                : `/Project/GetComments?feeStageId=${feestage.id}&monthYear=${monthYear}&displayType=${forecastType}`;

        const { data } = await execute<Note[]>('get', url);
        if (data) {
            const noteData = data
                .map((note) => {
                    const { commentID, commentText, createdByName, createdDate, updatedByName, updatedDate, isBaselineComment, baselineCommentType } = note;
                    return { "id": commentID, commentText, createdByName, createdDate, updatedByName, updatedDate, isBaselineComment, baselineCommentType };
                });

            setDataList(noteData);
        }
    };

    const columns: GridColDef<(typeof dataList)[number]>[] = [
         {
        field: 'isBaselineComment',
        headerName: 'BL',
        flex: 0.4,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (params) =>
            params.value ? (
                <span
                    style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: 'white', // ✅ medium green baseline color
                    color: '#a10000',
                    fontWeight: 700,
                    fontSize: '12px',
                    boxShadow: '0 0 4px rgba(163, 198, 61, 0.5)', // ✅ soft glow effect
                    textAlign: 'center',
                    lineHeight: '1',
                }}
                >
                    BL
                </span>
            ) : null,
    },
        {
            field: 'commentText', headerName: 'Notes/comments', flex: 2.5, headerAlign: 'center',
        },
        {
            field: 'createdDate', headerName: 'Created date', flex: 1, headerAlign: 'center',
            renderCell: (params: any) => (
                <div style={{ marginTop: 10, marginBottom: 8 }}>
                    {dayjs(params.value).format('DD/MM/YYYY HH:mm:ss')}
                </div>
            ),
        },
        {
            field: 'createdByName', headerName: 'Created by', flex: 1, headerAlign: 'center',
            renderCell: (params) => (
                <div style={{ marginTop: 10, marginBottom: 8 }}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'updatedDate', headerName: 'Last edited date', flex: 1, headerAlign: 'center',
            renderCell: (params: any) => (

                <div style={{ marginTop: 10, marginBottom: 8 }}>
                    {params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm:ss') : ''}
                </div>
            ),
        },
        {
            field: 'updatedByName', headerName: 'Last edited  by', flex: 1, headerAlign: 'center',
            renderCell: (params) => (
                <div style={{ marginTop: 10, marginBottom: 8 }}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'actions', headerName: 'Action', flex: 1, headerAlign: 'left',
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left', gap: 1, width: '100%',textAlign:"left" }}>
                    <IconButton
                        color="primary"
                        aria-label="edit"
                        onClick={() => {
                            setSelectedRow(params.row);
                            setFormData({ commentText: params.row.commentText });
                        }}
                    >
                        <EditCalendarIcon sx={{ color: '#a3c63d' }} ></EditCalendarIcon>
                    </IconButton>
                </div>
            ),
        },
    ];

    const handleNoteSubmit = async () => {
        const newErrors = { description: '' };
        if (!formData.commentText?.trim()) {
            newErrors.description = validationMessages.notesComments_required;
        } else if (formData.commentText.length > 5000) {
            newErrors.description = validationMessages.common_maxLength("Notes", 5000);
        }
        setErrors(newErrors);

        if (newErrors.description) return;

        let response: ApiResponse<any>;
        if (selectedRow) {
            const UpdatedNote = {
                ...selectedRow,
                commentText: formData.commentText,
                updatedBy: Number(userId),
            };
            response = await execute('put', `Project/UpdateComment/${selectedRow.id}`, UpdatedNote);
            showMessage(response);
            fetchComments();
        } else {

            const newNote = forecastType === ForecastDisplayType.OutStanding
                ? [{
                    feeStageId: feestage.id,
                    commentText: formData.commentText,
                    createdBy: Number(userId),
                    ...(isBaselineComment !== undefined && { isBaselineComment })
                }]
                : {
                    feeStageId: feestage.id,
                    commentText: formData.commentText,
                    createdBy: Number(userId),
                    monthYear: monthYear,
                    displayType: forecastType,
                    ...(isBaselineComment !== undefined && { isBaselineComment })
                };

            const url =
                forecastType === ForecastDisplayType.OutStanding
                    ? `/Project/Saveoutstanding-comments`
                    : `/Project/SaveComment`;
            await handleSave(newNote, url);
            await delay(1000); // Apply delay for 1 seconds if shouldDelay is true
            const commentAdded = forecastType === ForecastDisplayType.OutStanding
                ? true
                : !feestage.hasComments
            onClose(commentAdded);
        }
    };

    const handleSave = async (payload: any, url: any) => {
        const response: ApiResponse<any> = await execute('post', url, payload);
        showMessage(response)
    }

    const showMessage = async (response: any) => {
        if (response.error) {
            setToast({ open: true, message: response.error, type: "error" });
        } else {
            setToast({
                open: true,
                message: typeof response.data === "string" ? response.data : response.data.message || "Note added successfully",
                type: "success"
            });
            setFormData({ commentText: '' });
            setSelectedRow(null);
        }
    }

    return (
        <>
            {/* Modal Dialog */}
            <Dialog open={open}
                onClose={(e, reason) => {
                    if (feestage.isMandatory) return; // block closing entirely
                    onClose(false);
                }}
                maxWidth="xl"
                fullWidth
                disableEscapeKeyDown={feestage.isMandatory}
            >
                <DialogTitle
                    sx={{
                        position: 'relative',
                        color: '#1e66ab',
                        display: 'flex',
                        mb: 0,
                        fontWeight: 500,
                        fontSize: '24px',
                        pr: 5,
                    }}>

                    {`${feestage.feeStageReferenceNumber}: ${feestage.description} (${feestage.month})`}
                    {!feestage.isMandatory && (
                        <IconButton
                            color="inherit"
                            onClick={() => onClose(false)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}

                </DialogTitle>

                <Divider></Divider>

                <DialogContent>
                    <Box sx={{ flexGrow: 1 }}>
                        <Grid2 container spacing={3} sx={{ mb: 4 }}>
                            <Grid2 size={{ xs: 12, md: 9, lg: 9 }}>
                                <CustomTextField
                                    label="Notes/Comments"
                                    name="notes"
                                    value={formData.commentText}
                                    onChange={(e) => setFormData({ ...formData, commentText: e.target.value })}
                                    isTextArea
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderColor: formData.commentText ? '#1e66ab' : 'default',
                                            boxShadow: formData.commentText ? '0 0 5px rgba(30, 102, 171, 0.5)' : 'none',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: formData.commentText ? 'rgba(150, 193, 33, 0.75)' : 'default',
                                            borderWidth: formData.commentText ? '2px' : '1px',
                                        },
                                        '& .MuiInputBase-input': { backgroundColor: 'white' },
                                        '& textarea': { minHeight: '50px' }
                                    }}
                                />
                                {errors.description && (
                                    <Typography className="validationMessage" role="alert">{errors.description}</Typography>
                                )}
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 3 }} sx={{ mt: 3 }}>
                                <CustomButton
                                    capsule
                                    disabled={loading}
                                    sx={{ mt: 0, mb: 2, textTransform: 'none', whiteSpace: 'nowrap' }}
                                    onClick={handleNoteSubmit}
                                >
                                    {selectedRow ? 'Update Notes/Comments' : 'Add Notes/Comments'}
                                </CustomButton>
                            </Grid2>
                        </Grid2>

                        {dataList.length > 0 && (
                            <CustomDataGrid
                                rows={dataList}
                                columns={columns}
                                // setRowHeigt={true}
                                isDialogOpen={dialogOpen}
                                actionName={'Note'}
                                handleCancel={() => {
                                    setSelectedRow(null);
                                    setDialogOpen(false);
                                }}
                            // handleConfirm={handleDialogConfirm}
                            />
                        )}
                    </Box>

                    <Toaster
                        open={toast.open}
                        message={toast.message}
                        type={toast.type as "success" | "error"}
                        onClose={() => setToast({ ...toast, open: false })}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}

export default ForecastComment;

